import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import classNames from "classnames";

import { Button } from "../../../components/Button/Button";
import FileUpload from "../../../components/forms/FileUpload/FileUpload";
import { Card } from "../../../components/Card/Card";
import { Select } from "../../../components/forms/Select/Select";
import { TextInput } from "../../../components/forms/TextInput/TextInput";
import { DRINK_TYPES } from "../../../constants/styles";
import { htmlToPlainText } from "../textFormat";
import { countFilledFields, getReviewItems } from "../reviewItems";

interface SourceSectionProps {
  styleSource: "pdf" | "paste" | "manual";
  setStyleSource: (source: "pdf" | "paste" | "manual") => void;
  description: string;
  pasteText: string;
  setPasteText: (value: string) => void;
  isGuidelinesGenerated: boolean;
  isGenerating: boolean;
  triggerGuidelinesGeneration: (path?: string, name?: string) => Promise<void>;
  handleDocumentUpload: (files: any[]) => void;
  useApiClient: () => {
    scoresheetSchemas: {
      useListScoresheetSchemas: (params: any) => { data: any };
    };
  };
}

export const SourceSection: React.FC<SourceSectionProps> = ({
  styleSource,
  setStyleSource,
  description,
  pasteText,
  setPasteText,
  isGuidelinesGenerated,
  isGenerating,
  triggerGuidelinesGeneration,
  handleDocumentUpload,
  useApiClient,
}) => {
  const { t } = useTranslation();
  const { register, watch, setValue, control, formState: { errors } } = useFormContext();

  const api = useApiClient();

  const { data: scoresheetSchemasData } =
    api.scoresheetSchemas.useListScoresheetSchemas({
      filter: {
        state: "approved",
      },
    });

  const documentPath = watch("documentPath") || "";
  const documentName = watch("documentName") || "";
  const imageUrl = watch("imageUrl") || "";

  const [showReview, setShowReview] = useState(false);
  const formValues = watch();
  const reviewItems = getReviewItems(formValues, t);
  const { filled: filledFields, total: totalFields } = countFilledFields(formValues);
  const sourceCharacters = htmlToPlainText(description).length;

  return (
    <section className="bg-white/85 backdrop-blur-md border border-[#e5e0d8] shadow-sm transition-all duration-200 hover:border-[#8b4513]/40 p-6 rounded-xl space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold text-[#6c2f00]">
          {t("Pages:StyleCreator.Source.Title", "1. Style source")}
        </h2>
        {isGuidelinesGenerated ? (
          <span className="px-3 py-1 bg-[#b1f0ce] text-[#0e5138] text-xs font-semibold rounded-full flex items-center gap-1">
            <span className="icon-[ph--check-circle] text-xs" />
            {t("Pages:StyleCreator.Source.Processed", "Source processed")}
          </span>
        ) : (
          <span className="px-3 py-1 bg-[#fe932c]/20 text-[#6c2f00] text-xs font-semibold rounded-full flex items-center gap-1">
            <span className="icon-[ph--warning] text-xs" />
            {t("Pages:StyleCreator.Source.Required", "Source required")}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <TextInput
            label={t("Pages:StyleCreator.BasicInfo.Name", "Style name *")}
            required
            {...register("name", { required: true })}
            error={errors.name?.message as string}
          />
        </div>

        <div>
          <Controller
            control={control}
            name="drinkType"
            render={({ field: { onChange, onBlur, value } }) => (
              <Select
                id="drinkType"
                label={t(
                  "Pages:StyleCreator.BasicInfo.DrinkType",
                  "Drink type *",
                )}
                options={Object.entries(DRINK_TYPES).map(
                  ([val, label]) => ({
                    label: label as string,
                    value: val,
                  }),
                )}
                onChange={onChange}
                onBlur={onBlur}
                selected={value}
              />
            )}
          />
        </div>

        <div>
          <Controller
            control={control}
            name="scoresheetSchemaId"
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Select
                id="scoresheetSchemaId"
                label={t(
                  "Pages:StyleCreator.BasicInfo.ScoresheetSchema",
                  "Scoresheet schema *",
                )}
                options={
                  scoresheetSchemasData?.data?.map(
                    (scoresheetSchema: any) => ({
                      label: scoresheetSchema?.name,
                      value: scoresheetSchema?.id,
                    }),
                  ) || []
                }
                onChange={onChange}
                onBlur={onBlur}
                selected={value}
                error={errors.scoresheetSchemaId?.message as string}
              />
            )}
          />
        </div>
      </div>

      <div className="space-y-2">
        <span className="text-xs font-semibold text-[#877369] uppercase tracking-wider block">
          {t("Pages:StyleCreator.Source.Image", "Style image")}
        </span>
        <p className="text-xs text-[#54433a]">
          {t(
            "Pages:StyleCreator.Source.ImageHelp",
            "Shown on the style page. Upload your own graphic; when there is none, one is generated together with the guidelines.",
          )}
        </p>
        <div className="max-w-xs">
          <FileUpload
            directory="styles"
            acceptedTypes={["png", "jpg", "jpeg"]}
            maxSize={5 * 1024 * 1024}
            enforceRestrictions
            value={imageUrl}
            height={32}
            placeholderIcon="icon-[ph--image]"
            label={t("Pages:StyleCreator.Source.ImageDrop", "Drop an image here or click to upload")}
            onUpload={(files) =>
              setValue("imageUrl", files?.[0]?.url || "", { shouldTouch: true, shouldDirty: true })
            }
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          interactive
          variant={styleSource === "pdf" ? "glass" : "plain"}
          onClick={() => setStyleSource("pdf")}
          className={classNames(
            "cursor-pointer p-6 rounded-xl border transition-all hover:scale-[1.02] h-auto",
            styleSource === "pdf"
              ? "border-[#fe932c] bg-[#fff1e9]/60"
              : "border-[#dac2b6] bg-white",
          )}
        >
          <div className="col-span-12 flex flex-col items-center text-center">
            <span className="icon-[ph--upload-simple] text-[#fe932c] text-3xl mb-2" />
            <span className="font-bold text-[#221a14] block">
              {t("Pages:StyleCreator.Source.UploadPDF", "Upload PDF/DOCX")}
            </span>
            <span className="text-xs text-[#fe932c] font-bold mt-1 block">
              {t("Pages:StyleCreator.Source.Recommended", "Recommended")}
            </span>
            <span className="text-xs text-[#54433a] mt-1 block">
              {t(
                "Pages:StyleCreator.Source.ExtractHelper",
                "Extract text and detect fields",
              )}
            </span>
          </div>
        </Card>

        <Card
          interactive
          variant={styleSource === "paste" ? "glass" : "plain"}
          onClick={() => setStyleSource("paste")}
          className={classNames(
            "cursor-pointer p-6 rounded-xl border transition-all hover:scale-[1.02] h-auto",
            styleSource === "paste"
              ? "border-[#fe932c] bg-[#fff1e9]/60"
              : "border-[#dac2b6] bg-white",
          )}
        >
          <div className="col-span-12 flex flex-col items-center text-center">
            <span className="icon-[ph--clipboard-text] text-[#877369] text-3xl mb-2" />
            <span className="font-bold text-[#221a14] block">
              {t("Pages:StyleCreator.Source.Paste", "Paste description")}
            </span>
            <span className="text-xs text-[#54433a] mt-2 block">
              {t(
                "Pages:StyleCreator.Source.PasteHelper",
                "Paste style text from any source",
              )}
            </span>
          </div>
        </Card>

        <Card
          interactive
          variant={styleSource === "manual" ? "glass" : "plain"}
          onClick={() => setStyleSource("manual")}
          className={classNames(
            "cursor-pointer p-6 rounded-xl border transition-all hover:scale-[1.02] h-auto",
            styleSource === "manual"
              ? "border-[#fe932c] bg-[#fff1e9]/60"
              : "border-[#dac2b6] bg-white",
          )}
        >
          <div className="col-span-12 flex flex-col items-center text-center">
            <span className="icon-[ph--pencil-simple] text-[#877369] text-3xl mb-2" />
            <span className="font-bold text-[#221a14] block">
              {t("Pages:StyleCreator.Source.Manual", "Start manually")}
            </span>
            <span className="text-xs text-[#54433a] mt-2 block">
              {t(
                "Pages:StyleCreator.Source.ManualHelper",
                "Create from scratch without source",
              )}
            </span>
          </div>
        </Card>
      </div>

      {styleSource === "pdf" && (
        <div className="space-y-4">
          {documentPath ? (
            <div className="bg-[#f6e5db] p-4 rounded-xl flex items-center justify-between border border-[#e5e0d8] shadow-sm">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 bg-[#fe932c]/20 rounded-lg flex items-center justify-center text-[#fe932c] border border-[#fe932c]/30 flex-shrink-0">
                  <span className="icon-[ph--file-pdf] text-2xl" />
                </div>
                <div className="text-left min-w-0">
                  <div className="font-bold text-sm text-[#221a14] truncate">
                    {documentName || "document.pdf"}
                  </div>
                  <div className="text-xs text-[#54433a] mt-0.5 flex gap-2">
                    <Button
                      htmlType="button"
                      type="base"
                      variant="ghost"
                      onClick={() => window.open(documentPath, "_blank")}
                      className="p-0 text-[#6c2f00] hover:underline font-semibold flex items-center gap-0.5"
                    >
                      {t("Pages:StyleCreator.Source.ViewDocument", "View document")}
                      <span className="icon-[ph--arrow-square-out] text-[10px]" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  htmlType="button"
                  type="base"
                  variant="ghost"
                  onClick={() => triggerGuidelinesGeneration()}
                  disabled={isGenerating}
                  className="bg-white hover:bg-[#fff1e9] text-[#6c2f00] border border-[#dac2b6] font-bold text-xs py-2 px-4 rounded-lg flex items-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <span className="icon-[ph--spinner] text-sm animate-spin" />
                      {t("Pages:StyleCreator.Source.Extracting", "Extracting...")}
                    </>
                  ) : (
                    <>
                      <span className="icon-[ph--sparkle] text-sm" />
                      {t("Pages:StyleCreator.Source.ExtractAI", "Generate guidelines")}
                    </>
                  )}
                </Button>
                <Button
                  htmlType="button"
                  type="base"
                  variant="ghost"
                  onClick={() => {
                    setValue("documentPath", "");
                    setValue("documentName", "");
                  }}
                  className="text-[#54433a] hover:text-[#ba1a1a] p-2 rounded-full"
                >
                  <span className="icon-[ph--trash] text-lg" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-[#dac2b6] hover:border-[#fe932c] rounded-xl bg-white p-4 transition-all">
              <FileUpload
                directory="styles"
                acceptedTypes={["pdf", "docx"]}
                enforceRestrictions
                maxSize={10 * 1024 * 1024}
                onUpload={handleDocumentUpload}
                height={32}
                placeholderIcon="icon-[ph--file-pdf]"
                label={t("Pages:StyleCreator.Source.DragOrClick", "Drop PDF/DOCX file here or click to upload")}
              />
            </div>
          )}
        </div>
      )}

      {styleSource === "paste" && (
        <div className="space-y-4">
          <span className="text-xs font-semibold text-[#877369] uppercase tracking-wider block">
            {t("Pages:StyleCreator.Source.PasteDescription", "Paste description")}
          </span>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={t(
              "Pages:StyleCreator.Source.PastePlaceholder",
              "Paste description here...",
            )}
            rows={6}
            className="w-full rounded-xl border-[#dac2b6] bg-white focus:border-[#fe932c] focus:ring-[#fe932c] text-sm text-[#221a14] p-4 transition-all"
          />
          <div className="flex justify-between items-center">
            <span className="text-xs text-[#877369]">
              {pasteText.length} {t("Pages:StyleCreator.Source.Characters", "characters")}
            </span>
            <Button
              htmlType="button"
              type="base"
              variant="ghost"
              onClick={() => triggerGuidelinesGeneration()}
              disabled={isGenerating}
              className="bg-[#fe932c]/10 hover:bg-[#fe932c]/20 text-[#6c2f00] border border-[#fe932c]/30 font-bold text-xs py-2 px-4 rounded-lg flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <span className="icon-[ph--spinner] text-sm animate-spin" />
                  {t("Pages:StyleCreator.Source.Extracting", "Extracting...")}
                </>
              ) : (
                <>
                  <span className="icon-[ph--sparkle] text-sm" />
                  {t("Pages:StyleCreator.Source.ExtractAI", "Generate guidelines")}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {isGuidelinesGenerated && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-4 text-xs font-medium">
            <div className="flex items-center gap-1 text-[#034b32]">
              <span className="icon-[ph--arrows-clockwise] text-sm" />
              <span>
                {t("Pages:StyleCreator.Source.TextExtracted", "Description:")}{" "}
                <strong className="font-bold">
                  {t("Pages:StyleCreator.Source.CharactersCount", "{{count}} characters", {
                    count: sourceCharacters,
                  })}
                </strong>
              </span>
            </div>
            <div className="flex items-center gap-1 text-[#034b32]">
              <span className="icon-[ph--check-circle] text-sm" />
              <span>
                {t("Pages:StyleCreator.Source.FieldsAutofilled", "Parameters detected:")}{" "}
                <strong className="font-bold">
                  {t("Pages:StyleCreator.Source.FieldsFilled", "{{filled}} of {{total}} fields filled", {
                    filled: filledFields,
                    total: totalFields,
                  })}
                </strong>
              </span>
            </div>
            {reviewItems.length > 0 ? (
              <div className="flex items-center gap-1 text-[#904d00]">
                <span className="icon-[ph--warning] text-sm" />
                <span>
                  {t("Pages:StyleCreator.Source.Flagged", "Needs review:")}{" "}
                  <strong className="font-bold">
                    {t("Pages:StyleCreator.Source.FlaggedCount", "{{count}} items flagged", {
                      count: reviewItems.length,
                    })}
                  </strong>
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-[#034b32]">
                <span className="icon-[ph--check-circle] text-sm" />
                <span>{t("Pages:StyleCreator.Source.NothingToReview", "Nothing to review")}</span>
              </div>
            )}
            {reviewItems.length > 0 && (
              <Button
                htmlType="button"
                type="base"
                variant="ghost"
                onClick={() => setShowReview((open) => !open)}
                aria-expanded={showReview}
                className="ml-auto text-[#6c2f00] hover:underline font-bold text-xs p-0 flex items-center gap-0.5"
              >
                {showReview
                  ? t("Pages:StyleCreator.Source.HideDetails", "Hide details")
                  : t("Pages:StyleCreator.Source.ViewDetails", "View details")}
                <span
                  className={classNames(
                    "icon-[ph--caret-right] text-xs transition-transform",
                    showReview && "rotate-90",
                  )}
                />
              </Button>
            )}
          </div>

          {showReview && reviewItems.length > 0 && (
            <ul
              className="space-y-2 rounded-xl border border-[#dac2b6] bg-[#fff8f5] p-4"
              data-testid="review-items"
            >
              {reviewItems.map((item) => (
                <li key={item.id} className="flex gap-3 text-xs text-[#221a14]">
                  <span className="icon-[ph--warning] mt-0.5 flex-shrink-0 text-sm text-[#904d00]" />
                  <span>
                    <strong className="block font-bold">{item.title}</strong>
                    <span className="text-[#54433a]">{item.description}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};
